import React, { useState, useEffect, useRef } from 'react';
import styles from './style/CommentSection.module.css';
import { formatDistanceToNow } from 'date-fns';
import { ko, enUS } from 'date-fns/locale'; // 💡 다국어 지원을 위해 enUS도 임포트
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next'; // 💡 useTranslation 훅 임포트
import DOMPurify from 'dompurify';

const IconReply = () => (
  <svg className={styles.icon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 9L15 4M15 4L20 9M15 4V13C15 16.3137 12.3137 19 9 19H4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconEdit = () => (
  <svg className={styles.icon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 20H21M16.5 3.5L18.5 5.5M19 8L16.5 5.5L8 14L8 16L10 16L18.5 7.5L19 8ZM19 8L16.5 5.5L8 14L8 16L10 16L18.5 7.5L19 8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconDelete = () => (
  <svg className={styles.icon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 11V17M14 11V17M4 7H20M19 7L18.5 19.5C18.4172 21.0538 17.0538 22.2172 15.5 22.2172H8.5C6.94617 22.2172 5.58281 21.0538 5.5 19.5L5 7M10 4C10 2.89543 10.8954 2 12 2C13.1046 2 14 2.89543 14 4V7H10V4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DEFAULT_PROFILE_URL = process.env.REACT_APP_DEFAULT_PROFILE_IMAGE;

export default function CommentSection({ entityType, entityId, API_BASE_URL }) {
  const { t, i18n } = useTranslation(); // 💡 t 함수와 i18n 객체 사용
  const [comments, setComments] = useState([]);
  const [newUsername, setNewUsername] = useState('');
  const [newComment, setNewComment] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [passwordMap, setPasswordMap] = useState({});
  const [activePwdFor, setActivePwdFor] = useState(null);
  const [replyingFor, setReplyingFor] = useState(null);
  const [replyUserMap, setReplyUserMap] = useState({});
  const [replyTextMap, setReplyTextMap] = useState({});
  const [replyPwdMap, setReplyPwdMap] = useState({});
  const [delPwdMap, setDelPwdMap] = useState({});
  const [openRepliesMap, setOpenRepliesMap] = useState({});

  const deleteFormRef = useRef(null);
  const replyFormRef = useRef(null);
  const editFormRef = useRef(null);

  const auth = useAuth();
  const isAuthenticated = auth.isAuthenticated;
  const loggedInUserNickname = auth.user?.profile.name;
  const loggedInUserId = auth.user?.profile.sub;
  const idToken = auth.user?.id_token;

  const [profileImageUrl, setProfileImageUrl] = useState('');
  const currentLocale = i18n.language === 'ko' ? ko : enUS; // 💡 현재 언어에 맞는 locale 선택

  useEffect(() => {
    const fetchUserProfileImage = async () => {
      if (!isAuthenticated || !idToken) return;
      try {
        const res = await fetch(`${API_BASE_URL}/user/nickname/me`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${idToken}`,
          },
        });
        if (res.ok) {
          const data = await res.json();
          setProfileImageUrl(data.profileImageUrl || '');
        } else {
          console.error('Failed to fetch user profile for image:', res.status);
        }
      } catch (err) {
        console.error('Error fetching user profile image:', err);
      }
    };
    fetchUserProfileImage();
  }, [isAuthenticated, idToken, API_BASE_URL]);

  useEffect(() => {
    const isAnyOpen = activePwdFor !== null || replyingFor !== null || editingId !== null;
    if (!isAnyOpen) return;

    const handleClickOutside = e => {
      const tgt = e.target;
      if (
        (activePwdFor !== null && deleteFormRef.current?.contains(tgt)) ||
        (replyingFor !== null && replyFormRef.current?.contains(tgt)) ||
        (editingId !== null && editFormRef.current?.contains(tgt))
      ) return;

      setActivePwdFor(null);
      setReplyingFor(null);
      setEditingId(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activePwdFor, replyingFor, editingId]);

  const idToUser = React.useMemo(() => {
    const m = {};
    comments.forEach(c => { m[c.commentId] = c.username; });
    return m;
  }, [comments]);

  const calculateDepth = (comment, commentMap) => {
    let depth = 0;
    let current = comment;
    while (current.parentId) {
      depth++;
      current = commentMap[current.parentId];
      if (!current) break;
    }
    return depth;
  };

  const groupedComments = React.useMemo(() => {
    const map = { root: [] };
    const commentMap = {};
    comments.forEach(c => {
      commentMap[c.commentId] = c;
      if (!c.parentId) {
        map.root.push(c);
      }
    });
    comments.forEach(c => {
      if (c.parentId) {
        let parentId = c.parentId;
        let parent = commentMap[parentId];
        while (parent && parent.parentId) {
          parentId = parent.parentId;
          parent = commentMap[parentId];
        }
        if (!map[parentId]) map[parentId] = [];
        map[parentId].push({ ...c, depth: calculateDepth(c, commentMap) });
      }
    });
    Object.keys(map)
      .filter(parentId => parentId !== 'root')
      .forEach(parentId => {
        map[parentId].sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );
      });
    map.root.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    return map;
  }, [comments]);

  useEffect(() => {
    if (!entityId) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/comments/${entityId}`);
        if (!res.ok) throw new Error(t('commentSection.errors.fetchFailed'));
        const data = await res.json();
        setComments(data);
      } catch (err) {
        console.error(err);
      }
    })();
  }, [entityId, API_BASE_URL, t]);

  const handleSubmitComment = async () => {
    if (!entityId) {
      alert(t('commentSection.errors.noTarget'));
      return;
    }
    setIsSubmitting(true);
    try {
      let payload;
      let url;
      const headers = { 'Content-Type': 'application/json' };
      const sanitizedUsername = DOMPurify.sanitize(newUsername.trim(), { USE_PROFILES: { html: false } });
      const sanitizedComment = DOMPurify.sanitize(newComment.trim());

      if (isAuthenticated && idToken) {
        if (!sanitizedComment) {
          alert(t('commentSection.errors.commentRequired'));
          return;
        }
        payload = { text: sanitizedComment };
        url = `${API_BASE_URL}/comments/${entityId}/auth`;
        headers['Authorization'] = `Bearer ${idToken}`;
      } else {
        if (!sanitizedUsername || !sanitizedComment || !newPassword.trim()) {
          alert(t('commentSection.errors.allFieldsRequired'));
          return;
        }
        payload = { username: sanitizedUsername, text: sanitizedComment, password: newPassword };
        url = `${API_BASE_URL}/comments/${entityId}`;
      }

      const res = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const errorText = await res.text();
        let errorMessage = t('commentSection.errors.submitFailed');
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.message) errorMessage = errorJson.message;
          else if (errorJson.error) errorMessage = errorJson.error;
          else errorMessage = errorText;
        } catch (e) {
          errorMessage = errorText;
        }
        throw new Error(errorMessage);
      }
      const saved = await res.json();
      setComments(prev => [...prev, saved]);
      setNewUsername('');
      setNewComment('');
      setNewPassword('');
    } catch (err) {
      console.error('Error:', err);
      alert(`${t('commentSection.errors.networkError')}${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitReply = async parentId => {
    if (!entityId) {
      alert(t('commentSection.errors.noReplyTarget'));
      return;
    }
    const text = (replyTextMap[parentId] || '').trim();
    if (!text) {
      alert(t('commentSection.errors.replyRequired'));
      return;
    }
    const sanitizedText = DOMPurify.sanitize(text);

    try {
      let payload;
      let url;
      const headers = { 'Content-Type': 'application/json' };
      if (isAuthenticated && idToken) {
        payload = { text: sanitizedText, parentId: parentId };
        url = `${API_BASE_URL}/comments/${entityId}/auth`;
        headers['Authorization'] = `Bearer ${idToken}`;
      } else {
        const username = (replyUserMap[parentId] || '').trim();
        const password = (replyPwdMap[parentId] || '').trim();
        const sanitizedUsername = DOMPurify.sanitize(username, { USE_PROFILES: { html: false } });

        if (!sanitizedUsername || !password) {
          alert(t('commentSection.errors.replyFieldsRequired'));
          return;
        }
        payload = { username: sanitizedUsername, text: sanitizedText, password, parentId };
        url = `${API_BASE_URL}/comments/${entityId}`;
      }

      const res = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const errorText = await res.text();
        let errorMessage = t('commentSection.errors.replyFailed');
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.message) errorMessage = errorJson.message;
          else if (errorJson.error) errorMessage = errorJson.error;
          else errorMessage = errorText;
        } catch (e) {
          errorMessage = errorText;
        }
        throw new Error(errorMessage);
      }
      const saved = await res.json();
      setComments(prev => [...prev, saved]);
      setReplyUserMap(m => ({ ...m, [parentId]: '' }));
      setReplyTextMap(m => ({ ...m, [parentId]: '' }));
      setReplyPwdMap(m => ({ ...m, [parentId]: '' }));
      setReplyingFor(null);
    } catch (err) {
      console.error('Error:', err);
      alert(`${t('commentSection.errors.networkError')}${err.message}`);
    }
  };

  const handleSaveEdit = async cId => {
    if (!entityId) {
      alert(t('commentSection.errors.noEditTarget'));
      return;
    }
    const commentToEdit = comments.find(c => c.commentId === cId);
    if (!commentToEdit) return;

    if (!editText.trim()) {
      alert(t('commentSection.errors.editTextRequired'));
      return;
    }

    const sanitizedEditText = DOMPurify.sanitize(editText);

    try {
      let url;
      const headers = { 'Content-Type': 'application/json' };
      const payload = { text: sanitizedEditText };

      if (commentToEdit.isAnonymous) {
        const pw = (passwordMap[cId] || '').trim();
        if (!pw) {
          alert(t('commentSection.errors.passwordRequired'));
          return;
        }
        payload.password = pw;
        url = `${API_BASE_URL}/comments/${entityId}/${cId}`;
      } else {
        if (!isAuthenticated || !idToken) {
          alert(t('commentSection.errors.authRequired'));
          return;
        }
        url = `${API_BASE_URL}/comments/${entityId}/auth/${cId}`;
        headers['Authorization'] = `Bearer ${idToken}`;
      }

      const res = await fetch(url, {
        method: 'PUT',
        headers: headers,
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const errorText = await res.text();
        let errorMessage = t('commentSection.errors.editFailed');
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.message) errorMessage = errorJson.message;
          else if (errorJson.error) errorMessage = errorJson.error;
          else errorMessage = errorText;
        } catch (e) {
          errorMessage = errorText;
        }
        throw new Error(errorMessage);
      }
      const updated = await res.json();
      setComments(cs => cs.map(c => c.commentId === cId ? updated : c));
      setEditingId(null);
      setPasswordMap(m => { const next = { ...m }; delete next[cId]; return next; });
    } catch (err) {
      console.error('Error:', err);
      alert(`${t('commentSection.errors.networkError')}${err.message}`);
    }
  };

  const handleDelete = async cId => {
    if (!entityId) {
      alert(t('commentSection.errors.noDeleteTarget'));
      return;
    }
    const commentToDelete = comments.find(c => c.commentId === cId);
    if (!commentToDelete) return;
    if (!window.confirm(t('commentSection.info.confirmDelete'))) return;

    try {
      let url;
      const headers = { 'Content-Type': 'application/json' };
      const payload = {};
      if (commentToDelete.isAnonymous) {
        const pw = (delPwdMap[cId] || '').trim();
        if (!pw) {
          alert(t('commentSection.errors.passwordRequired'));
          return;
        }
        payload.password = pw;
        url = `${API_BASE_URL}/comments/${entityId}/${cId}`;
      } else {
        if (!isAuthenticated || !idToken) {
          alert(t('commentSection.errors.authRequired'));
          return;
        }
        url = `${API_BASE_URL}/comments/${entityId}/auth/${cId}`;
        headers['Authorization'] = `Bearer ${idToken}`;
      }

      const res = await fetch(url, {
        method: 'DELETE',
        headers: headers,
        body: JSON.stringify(payload)
      });
      if (res.status !== 204) {
        const errorText = await res.text();
        let errorMessage = t('commentSection.errors.deleteFailed');
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.message) errorMessage = errorJson.message;
          else if (errorJson.error) errorMessage = errorJson.error;
          else errorMessage = errorText;
        } catch (e) {
          errorMessage = errorText;
        }
        throw new Error(errorMessage);
      }
      setComments(cs => cs.filter(c => c.commentId !== cId));
      setDelPwdMap(m => { const next = { ...m }; delete next[cId]; return next; });
      setActivePwdFor(null);
    } catch (err) {
      console.error('Error:', err);
      alert(`${t('commentSection.errors.networkError')}${err.message}`);
    }
  };

  const renderComment = (c, depth = 0) => {
    const children = groupedComments[c.commentId] || [];
    const replyCount = children.length;
    const isOpen = openRepliesMap[c.commentId];
    const isReplying = replyingFor === c.commentId;
    const isEditing = editingId === c.commentId;
    const isDel = activePwdFor === c.commentId;
    const isNested = depth > 1;

    const isOwner = isAuthenticated && c.authorId === loggedInUserId;
    const canEditOrDelete = c.isAnonymous || isOwner;

    return (
      <div
        key={c.commentId}
        style={{ marginLeft: depth >= 1 ? 20 : 0 }}
      >
        <div
          className={
            c.parentId
              ? `${styles.comment} ${styles.replyBg}`
              : styles.comment
          }
        >
          <div className={styles.commentHeader}>
            {!c.isAnonymous && (
              <div className={styles.profileInfo}>
                <img
                  src={c.profileImageUrl || DEFAULT_PROFILE_URL}
                  alt={t('profile')}
                  className={styles.profileImage}
                />
              </div>
            )}
            <div className={styles.userInfo}>
              <strong>{DOMPurify.sanitize(c.username, { USE_PROFILES: { html: false } })}</strong>
              <span className={styles.commentDate} title={new Date(c.createdAt).toLocaleString(i18n.language)}>
                {formatDistanceToNow(new Date(c.createdAt), { addSuffix: true, locale: currentLocale })}
              </span>
            </div>
          </div>

          {isEditing && canEditOrDelete ? (
            <div
              ref={editFormRef}
              className={styles.commentEditForm}
              onClick={e => e.stopPropagation()}
            >
              <textarea
                className={styles.commentInput}
                value={editText}
                onChange={e => setEditText(e.target.value)}
              />
              {c.isAnonymous && (
                <input
                  type="password"
                  placeholder={t('commentSection.placeholder.password')}
                  className={styles.commentPwd}
                  value={passwordMap[c.commentId] || ''}
                  onChange={e => setPasswordMap(m => ({ ...m, [c.commentId]: e.target.value }))}
                />
              )}
              <div className={styles.replyActions}>
                <button onClick={() => handleSaveEdit(c.commentId)}>{t('commentSection.button.save')}</button>
                <button onClick={() => setEditingId(null)}>{t('commentSection.button.cancel')}</button>
              </div>
            </div>
          ) : (
            <p className={styles.commentText}>
              {isNested && (
                <span className={styles.replyTag}>@{DOMPurify.sanitize(idToUser[c.parentId], { USE_PROFILES: { html: false } })}</span>
              )}
              {DOMPurify.sanitize(c.text)}
            </p>
          )}

          {isDel && canEditOrDelete ? (
            <div
              ref={deleteFormRef}
              className={styles.deleteForm}
              onClick={e => e.stopPropagation()}
            >
              {c.isAnonymous && (
                <input
                  type="password"
                  placeholder={t('commentSection.placeholder.password')}
                  value={delPwdMap[c.commentId] || ''}
                  onChange={e => setDelPwdMap(m => ({ ...m, [c.commentId]: e.target.value }))}
                />
              )}
              <div className={styles.replyActions}>
                <button onClick={() => handleDelete(c.commentId)}>{t('commentSection.button.delete')}</button>
                <button onClick={() => setActivePwdFor(null)}>{t('commentSection.button.cancel')}</button>
              </div>
            </div>
          ) : null}

          <div className={styles.commentFooter}>
            {replyCount > 0 && (
              <button
                className={styles.btnToggleReplies}
                onClick={() => setOpenRepliesMap(prev => ({ ...prev, [c.commentId]: !prev[c.commentId] }))}
              >
                {isOpen ? t('commentSection.button.hideReplies') : t('commentSection.button.showReplies', { count: replyCount })}
              </button>
            )}
            <div className={styles.footerActions}>
              <button
                className={`${styles.btnAction} ${styles.btnReply}`}
                onClick={() => setReplyingFor(isReplying ? null : c.commentId)}
              >
                <IconReply />
                <span>{t('commentSection.button.reply')}</span>
              </button>
              {canEditOrDelete && (
                <>
                  <button
                    className={`${styles.btnAction} ${styles.btnEdit}`}
                    onClick={() => {
                      setEditingId(isEditing ? null : c.commentId);
                      setEditText(c.text);
                      setActivePwdFor(null);
                      setReplyingFor(null);
                    }}
                  >
                    <IconEdit />
                    <span>{t('commentSection.button.edit')}</span>
                  </button>
                  <button
                    className={`${styles.btnAction} ${styles.btnDelete}`}
                    onClick={e => {
                      e.stopPropagation();
                      setActivePwdFor(c.commentId);
                      setEditingId(null);
                      setReplyingFor(null);
                    }}
                  >
                    <IconDelete />
                    <span>{t('commentSection.button.delete')}</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {isReplying && (
          <div
            ref={replyFormRef}
            className={styles.replyForm}
            onClick={e => e.stopPropagation()}
          >
            {isAuthenticated ? (
              <>
                <p className={styles.loggedInAs}>{t('commentSection.info.loggedInAs', { username: DOMPurify.sanitize(loggedInUserNickname || t('commentSection.info.anonymousUser')) })}</p>
                <textarea
                  placeholder={t('commentSection.placeholder.reply')}
                  value={replyTextMap[c.commentId] || ''}
                  onChange={e => setReplyTextMap(m => ({ ...m, [c.commentId]: e.target.value }))}
                />
              </>
            ) : (
              <>
                <input
                  placeholder={t('commentSection.placeholder.username')}
                  value={replyUserMap[c.commentId] || ''}
                  onChange={e => setReplyUserMap(m => ({ ...m, [c.commentId]: e.target.value }))}
                />
                <textarea
                  placeholder={t('commentSection.placeholder.reply')}
                  value={replyTextMap[c.commentId] || ''}
                  onChange={e => setReplyTextMap(m => ({ ...m, [c.commentId]: e.target.value }))}
                />
                <input
                  type="password"
                  placeholder={t('commentSection.placeholder.password')}
                  value={replyPwdMap[c.commentId] || ''}
                  onChange={e => setReplyPwdMap(m => ({ ...m, [c.commentId]: e.target.value }))}
                />
              </>
            )}
            <div className={styles.replyActions}>
              <button onClick={() => handleSubmitReply(c.commentId)}>{t('commentSection.button.submit')}</button>
              <button onClick={() => setReplyingFor(null)}>{t('commentSection.button.cancel')}</button>
            </div>
          </div>
        )}
        {isOpen && children.map(child => renderComment(child, child.depth))}
      </div>
    );
  };

  return (
    <div className={styles.commentSection}>
      <h3 className={styles.subTitle}>{t('commentSection.title')}</h3>
      <div className={styles.commentList}>
        {groupedComments['root']?.length === 0 ? (
          <p className={styles.commentEmpty}>{t('commentSection.empty')}</p>
        ) : (
          (groupedComments['root'] || [])
            .map(c => renderComment(c, 0))
        )}
      </div>
      <div className={styles.newCommentForm}>
        {auth.isLoading ? (
          <p className={styles.loadingAuth}>{t('commentSection.info.loadingAuth')}</p>
        ) : isAuthenticated ? (
          <>
            <div className={styles.profileInfo}>
              <img
                src={profileImageUrl || DEFAULT_PROFILE_URL}
                alt={t('profile')}
                className={styles.profileImage}
              />
              <div className={styles.userInfo}>
                <strong>{DOMPurify.sanitize(loggedInUserNickname || t('commentSection.info.anonymousUser'))}</strong>
              </div>
            </div>
            <textarea
              className={styles.commentInput}
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              placeholder={t('commentSection.placeholder.newComment')}
            />
            <button
              className={styles.btnSubmit}
              onClick={handleSubmitComment}
              disabled={isSubmitting}
            >
              {isSubmitting ? t('commentSection.button.submitting') : t('commentSection.button.submit')}
            </button>
          </>
        ) : (
          <>
            <input
              className={styles.commentUsername}
              value={newUsername}
              onChange={e => setNewUsername(e.target.value)}
              placeholder={t('commentSection.placeholder.username')}
            />
            <textarea
              className={styles.commentInput}
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              placeholder={t('commentSection.placeholder.newComment')}
            />
            <input
              type="password"
              placeholder={t('commentSection.placeholder.password')}
              className={styles.commentPwd}
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
            />
            <button
              className={styles.btnSubmit}
              onClick={handleSubmitComment}
              disabled={isSubmitting}
            >
              {isSubmitting ? t('commentSection.button.submitting') : t('commentSection.button.submit')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}